/**
 * License-plate OCR scanner.
 *
 * Two backends, picked automatically by platform:
 *
 *   - **ML Kit (native)** — on Android via Capacitor we use Google's on-device
 *     ML Kit Text Recognition. Much faster (~50-100ms/frame) and far more
 *     accurate on noisy camera frames than JS-side OCR.
 *
 *   - **Tesseract.js (web)** — fallback when we're running in a browser
 *     (e.g. `npm run dev`), with image preprocessing (grayscale + Otsu
 *     threshold) so the digits are high-contrast before OCR.
 *
 * Both paths run a periodic auto-scan: capture the centre band of the
 * camera frame, send it to OCR, then require N consecutive matching
 * detections before accepting a plate (de-flickers the result).
 *
 * Uses the existing `#plateScannerModal` markup in index.html.
 *
 * Public API matches the legacy `window.PlateScanner` object:
 *   PlateScanner.open()
 *   PlateScanner.close()
 *   PlateScanner.openManualInput()
 *   PlateScanner.usePlate(plate)
 */

import { createLogger } from '../../core/logger.js';
import { normalize as normalizePlate } from '../../shared/plate.js';

const log = createLogger('plate-scanner');

/** Whether we should use ML Kit (native Capacitor) or Tesseract (web). */
function isNative() {
  const cap = /** @type {any} */ (window).Capacitor;
  return !!cap?.isNativePlatform?.();
}

const IDS = /** @type {const} */ ({
  modal: 'plateScannerModal',
  video: 'scannerVideo',
  preview: 'scannerPreview',
  progressContainer: 'scannerProgressContainer',
  progressBar: 'scannerProgress',
  progressText: 'scannerProgressText',
  result: 'scannerResult',
});

/** How often we grab a frame for OCR. Too aggressive thrashes Tesseract;
 *  too lazy and the user thinks nothing's happening. 600ms feels live. */
const SCAN_INTERVAL_MS = 600;
/** Same plate must appear in this many consecutive scans before we accept
 *  it. Two is enough — OCR confidence on a single frame is unreliable but
 *  agreement across two frames almost always means it's right. */
const REQUIRED_DETECTIONS = 2;

const TESSERACT_CDN = 'https://cdn.jsdelivr.net/npm/tesseract.js@4/dist/tesseract.min.js';

/** @type {any} */
let worker = null;
let workerReady = false;
/** @type {MediaStream | null} */
let videoStream = null;
/** @type {ReturnType<typeof setInterval> | null} */
let scanTimer = null;
let isOpen = false;
let lastDetected = '';
let detectionCount = 0;

export async function open() {
  if (isOpen) return;
  const modal = document.getElementById(IDS.modal);
  if (!modal) {
    log.error('#plateScannerModal not found in DOM');
    return;
  }
  isOpen = true;
  lastDetected = '';
  detectionCount = 0;
  clearResult();
  setProgress(0, 'מאתחל…');

  modal.classList.add('open');
  modal.style.display = 'flex';
  document.body.classList.add('no-scroll');

  // Start the camera FIRST so the user sees the live feed within a second,
  // then load Tesseract in parallel. The old order (worker → camera) meant
  // the user stared at a black rectangle while Tesseract downloaded from
  // CDN, and any Tesseract failure killed the camera too.
  let cameraOk = false;
  try {
    setProgress(10, 'מבקש הרשאת מצלמה...');
    await ensureCameraPermission();
    setProgress(20, 'פותח מצלמה...');
    await startCamera();
    cameraOk = true;
    setProgress(40, 'מצלמה פעילה — טוען מנוע זיהוי...');
  } catch (err) {
    log.warn('camera failed:', err);
    setProgress(0, 'לא ניתן לפתוח מצלמה — נסה הקלדה ידנית.');
  }

  if (!cameraOk) return; // Don't waste time on OCR if no camera.

  // On native we don't need to download a 30MB Tesseract model — ML Kit is
  // built into the APK. Skip straight to scanning.
  if (isNative()) {
    workerReady = true; // sentinel — tryScanFrame() checks this flag.
    startAutoScan();
    setProgress(50, 'מוכן — הצמד את הלוחית למסגרת');
    return;
  }

  try {
    await ensureWorker();
    startAutoScan();
    setProgress(50, 'מוכן — הצמד את הלוחית למסגרת');
  } catch (err) {
    log.warn('worker init failed:', err);
    setProgress(0, 'שגיאה במנוע הזיהוי — נסה הקלדה ידנית.');
  }
}

/**
 * On native (Capacitor), explicitly request camera permission via the
 * @capacitor/camera plugin so the user sees the standard Android dialog.
 * On the browser, getUserMedia handles the prompt itself.
 */
async function ensureCameraPermission() {
  const cap = /** @type {any} */ (window).Capacitor;
  if (!cap?.isNativePlatform?.()) return; // web — browser handles permission
  const Camera = cap.Plugins?.Camera;
  if (!Camera) return;
  try {
    const perm = await Camera.checkPermissions();
    if (perm.camera !== 'granted') {
      const req = await Camera.requestPermissions({ permissions: ['camera'] });
      if (req.camera !== 'granted') throw new Error('camera permission denied');
    }
  } catch (err) {
    log.warn('Capacitor camera permission check failed:', err);
    // Don't throw — getUserMedia might still work via WebView.
  }
}

export async function close() {
  if (!isOpen) return;
  isOpen = false;
  stopAutoScan();
  stopCamera();
  await disposeWorker();

  const modal = document.getElementById(IDS.modal);
  if (modal) {
    modal.classList.remove('open');
    modal.style.display = 'none';
  }
  document.body.classList.remove('no-scroll');
}

/** Switch to manual entry — closes the scanner and focuses the search input. */
export function openManualInput() {
  void close();
  setTimeout(() => {
    const searchInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById('searchPlate')
    );
    if (searchInput) {
      searchInput.focus();
      searchInput.select();
    }
  }, 120);
}

/**
 * Submit the detected plate to the search flow. Closes the scanner.
 * @param {string} plate
 */
export function usePlate(plate) {
  const normalized = normalizePlate(plate);
  const searchInput = /** @type {HTMLInputElement | null} */ (
    document.getElementById('searchPlate')
  );
  if (searchInput) {
    searchInput.value = normalized;
    searchInput.dispatchEvent(new Event('input', { bubbles: true }));
  }
  void close();
  // Auto-trigger search if the legacy function is present.
  setTimeout(() => {
    const w = /** @type {any} */ (window);
    try { w.searchVehicle?.(); }
    catch (e) { log.warn('searchVehicle:', e); }
  }, 250);
}

// ====================================================================
// internals — worker
// ====================================================================

async function ensureWorker() {
  if (workerReady && worker) return;
  await loadTesseractIfNeeded();
  const Tesseract = /** @type {any} */ (window).Tesseract;
  if (!Tesseract) throw new Error('Tesseract.js not available');
  setProgress(20, 'יוצר מנוע זיהוי...');
  worker = await Tesseract.createWorker('eng', 1, {
    logger: (/** @type {{ status: string; progress: number }} */ m) => {
      if (m.status === 'recognizing text') {
        setProgress(60 + Math.round(m.progress * 40), 'מעבד...');
      }
    },
  });
  // PSM 7 = "Treat the image as a single text line" — best for plate
  // scanning, where the camera frame is cropped to just the plate strip.
  // We DON'T set `tessedit_char_whitelist` because in v4-LSTM mode it
  // sometimes forces low-confidence empty output; `extractPlate` already
  // filters to digits client-side.
  await worker.setParameters({ tessedit_pageseg_mode: '7' });
  workerReady = true;
  setProgress(50, 'מוכן לסריקה');
}

async function disposeWorker() {
  if (!worker) {
    workerReady = false;
    return;
  }
  try {
    await worker.terminate();
  } catch (err) {
    log.warn('worker.terminate threw:', err);
  } finally {
    worker = null;
    workerReady = false;
  }
}

async function loadTesseractIfNeeded() {
  if (/** @type {any} */ (window).Tesseract) return;
  await new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-tesseract]');
    if (existing) {
      existing.addEventListener('load', () => resolve(undefined), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = TESSERACT_CDN;
    s.setAttribute('data-tesseract', '1');
    s.onload = () => resolve(undefined);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

// ====================================================================
// internals — camera
// ====================================================================

async function startCamera() {
  const video = /** @type {HTMLVideoElement | null} */ (document.getElementById(IDS.video));
  if (!video) throw new Error('#scannerVideo not found');
  try {
    videoStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false,
    });
    video.srcObject = videoStream;
    await video.play();
  } catch (err) {
    log.warn('camera failed:', err);
    throw err;
  }
}

function stopCamera() {
  if (videoStream) {
    for (const track of videoStream.getTracks()) {
      try { track.stop(); } catch { /* ignore */ }
    }
    videoStream = null;
  }
  const video = /** @type {HTMLVideoElement | null} */ (document.getElementById(IDS.video));
  if (video) {
    try { video.pause(); } catch { /* ignore */ }
    video.srcObject = null;
  }
}

// ====================================================================
// internals — auto-scan
// ====================================================================

function startAutoScan() {
  stopAutoScan();
  scanTimer = setInterval(() => { void tryScanFrame(); }, SCAN_INTERVAL_MS);
}

function stopAutoScan() {
  if (scanTimer) { clearInterval(scanTimer); scanTimer = null; }
}

async function tryScanFrame() {
  // ML Kit path has no Tesseract worker, so we only require `worker` when
  // running in the browser. The `workerReady` flag is set in both paths
  // (sentinel on native, real-readiness on web).
  if (!isOpen || !workerReady || !videoStream) {
    setProgress(50, `הסורק לא מוכן (open=${isOpen} ready=${workerReady} stream=${!!videoStream})`);
    return;
  }
  if (!isNative() && !worker) return;
  const video = /** @type {HTMLVideoElement | null} */ (document.getElementById(IDS.video));
  if (!video) { setProgress(50, '#scannerVideo חסר ב-DOM'); return; }
  if (video.readyState < 2) { setProgress(50, `ממתין למצלמה (readyState=${video.readyState})`); return; }
  if (video.videoWidth === 0) { setProgress(50, 'המצלמה לא החזירה פריימים — נסה שוב'); return; }

  // Crop the centre band of the frame — wider than before (85% × 35%) so the
  // user has more leeway in positioning the plate, and so motion blur on
  // the edges doesn't kill the whole frame.
  const canvas = document.createElement('canvas');
  const cropH = Math.round(video.videoHeight * 0.35);
  const cropY = Math.round(video.videoHeight * 0.40);
  const cropW = Math.round(video.videoWidth * 0.85);
  const cropX = Math.round(video.videoWidth * 0.075);

  // Scale the cropped image up to a comfortable OCR size — Tesseract reads
  // small text very poorly. Target ~1000px wide regardless of camera input.
  const targetW = Math.max(800, Math.min(1400, cropW));
  const scale = targetW / cropW;
  canvas.width = Math.round(cropW * scale);
  canvas.height = Math.round(cropH * scale);

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;
  // High-quality downsample. We apply heavy binarisation preprocessing
  // ONLY for the Tesseract path — ML Kit is trained on real-world photos
  // and works BETTER on the raw colour image (binarising loses fine
  // glyph edges it relies on).
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height);
  if (!isNative()) {
    preprocessForOcr(ctx, canvas.width, canvas.height);
  }

  let text = '';
  try {
    text = await ocrCanvas(canvas);
  } catch (err) {
    const msg = /** @type {Error} */ (err)?.message ?? String(err);
    console.error('[scanner] OCR threw:', err);
    setProgress(60, `שגיאת OCR: ${msg.slice(0, 40)}`);
    return;
  }
  const plate = extractPlate(text);

  // Always show what the OCR saw — even garbage — so the user (and we) can
  // tell whether it's an OCR-quality problem or a "no plate in view" problem.
  // The legacy connection-manager silences `console.warn`, so this goes to
  // `error` to remain visible during diagnostic work.
  const snippet = text.replace(/\s+/g, ' ').trim().slice(0, 24) || '(empty)';
  if (!plate) {
    setProgress(60, `OCR: "${snippet}" — לא נמצאה לוחית`);
    return;
  }

  if (plate === lastDetected) {
    detectionCount += 1;
    setProgress(80, `מאתר ${plate}... (${detectionCount}/${REQUIRED_DETECTIONS})`);
    if (detectionCount >= REQUIRED_DETECTIONS) {
      onPlateConfirmed(plate);
    }
  } else {
    lastDetected = plate;
    detectionCount = 1;
    setProgress(70, `זוהה: ${plate} (1/${REQUIRED_DETECTIONS})`);
  }
}

/**
 * Run OCR on the canvas — ML Kit on native, Tesseract on web. Returns the
 * recognized text (possibly multi-line / messy — `extractPlate` picks the
 * plate digits out).
 *
 * @param {HTMLCanvasElement} canvas
 * @returns {Promise<string>}
 */
async function ocrCanvas(canvas) {
  if (isNative()) {
    return await ocrCanvasMlKit(canvas);
  }
  if (!worker) return '';
  const recognized = await worker.recognize(canvas);
  return String(recognized?.data?.text ?? '');
}

/**
 * Convert canvas to base64 and hand it to the ML Kit plugin. Returns the
 * concatenated text from all detected blocks. ~50-100ms on a modern phone.
 *
 * @param {HTMLCanvasElement} canvas
 * @returns {Promise<string>}
 */
async function ocrCanvasMlKit(canvas) {
  await ensureMlKitLoaded();
  // Read the plugin from the module-level variable — DO NOT return it
  // from an async function (see the comment on `mlKitPlugin`).
  if (!mlKitPlugin) throw new Error('ML Kit plugin not loaded');
  const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
  // ML Kit wants raw base64 without the `data:image/jpeg;base64,` prefix.
  const base64Image = dataUrl.replace(/^data:[^;]+;base64,/, '');
  const result = await mlKitPlugin.detectText({ base64Image, rotation: 0 });
  return String(result?.text ?? '');
}

/**
 * The ML Kit plugin object is itself a Capacitor Proxy — every property
 * access on it is treated as a native method call. That includes `.then`,
 * which JS calls automatically when an async function `return`s the proxy
 * (the runtime checks "is this thenable?" by reading `.then`, and Capacitor
 * routes that to a non-existent native `.then()` method, throwing
 * `CapacitorException: "X.then()" is not implemented`).
 *
 * We sidestep the trap by storing the plugin in a module-level variable
 * (not returning it from any async function) and reading it directly.
 *
 * @type {any}
 */
let mlKitPlugin = null;
let mlKitTriedLoading = false;

/**
 * One-shot init — populates the module-level `mlKitPlugin` reference, then
 * resolves `void` (NOT the proxy itself). Safe to call from any number of
 * places concurrently; the resolved import is memoised.
 */
async function ensureMlKitLoaded() {
  if (mlKitPlugin || mlKitTriedLoading) return;
  mlKitTriedLoading = true;

  // Try the ES-module import first (most reliable when Vite bundles the
  // plugin into the app bundle).
  try {
    const mod = await import('@pantrist/capacitor-plugin-ml-kit-text-recognition');
    mlKitPlugin = mod.CapacitorPluginMlKitTextRecognition;
  } catch (err) {
    console.error('[scanner] ML Kit ES import failed:', err);
  }

  // Fallback: every Capacitor plugin also registers itself on
  // `window.Capacitor.Plugins.<PluginName>`. Use that if the import didn't
  // give us anything.
  if (!mlKitPlugin) {
    const cap = /** @type {any} */ (window).Capacitor;
    const fromRegistry =
      cap?.Plugins?.CapacitorPluginMlKitTextRecognition ??
      cap?.Plugins?.MlKitTextRecognition;
    if (fromRegistry) {
      console.error('[scanner] ML Kit obtained from window.Capacitor.Plugins fallback');
      mlKitPlugin = fromRegistry;
    } else {
      console.error(
        '[scanner] ML Kit not in registry either. Available plugins:',
        Object.keys(cap?.Plugins ?? {}),
      );
    }
  }
}

/**
 * Image preprocessing for OCR — Tesseract reads cleaner contrast much
 * better than raw camera frames. We:
 *   1. Convert to grayscale (weights tuned for human luminance perception)
 *   2. Pass through Otsu-like adaptive threshold so dark text on the
 *      yellow Israeli plate becomes pure black on pure white.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} w
 * @param {number} h
 */
function preprocessForOcr(ctx, w, h) {
  const img = ctx.getImageData(0, 0, w, h);
  const px = img.data;

  // Pass 1: grayscale + collect histogram for Otsu threshold.
  /** @type {number[]} */
  const hist = new Array(256).fill(0);
  for (let i = 0; i < px.length; i += 4) {
    const g = (px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114) | 0;
    px[i] = px[i + 1] = px[i + 2] = g;
    hist[g]++;
  }

  // Otsu's method — find the gray value that maximises between-class variance.
  const total = (px.length / 4) | 0;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, maxVar = 0, threshold = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const v = wB * wF * (mB - mF) * (mB - mF);
    if (v > maxVar) { maxVar = v; threshold = t; }
  }

  // Pass 2: binarise (pull contrast through the noise floor).
  for (let i = 0; i < px.length; i += 4) {
    const v = px[i] >= threshold ? 255 : 0;
    px[i] = px[i + 1] = px[i + 2] = v;
  }
  ctx.putImageData(img, 0, 0);
}

/**
 * Pull the most plausible Israeli license plate from OCR text. Plates are
 * 6-8 digits, commonly displayed with hyphens (`XX-XXX-XX` or `XXX-XX-XXX`),
 * so we look for runs of digits-or-hyphens, strip the hyphens, then keep
 * the candidate(s) with the right digit count. Prefer the longest candidate
 * (8 > 7 > 6) when the OCR text contains multiple plausible runs.
 *
 * @param {string} ocrText
 * @returns {string}
 */
function extractPlate(ocrText) {
  const candidates = (ocrText.match(/\d[\d\-‐-― ]{4,}\d/g) || [])
    .map((s) => s.replace(/\D/g, ''))
    .filter((s) => s.length >= 6 && s.length <= 8);
  if (candidates.length === 0) {
    // Fallback: maybe OCR returned the digits without any separator (e.g.
    // a perfectly clean "62194702" reading). Strip everything non-digit
    // from the original text.
    const stripped = ocrText.replace(/\D/g, '');
    if (stripped.length >= 6 && stripped.length <= 8) return stripped;
    return '';
  }
  candidates.sort((a, b) => b.length - a.length);
  return candidates[0];
}

/** @param {string} plate */
function onPlateConfirmed(plate) {
  stopAutoScan();
  setProgress(100, `זוהה: ${plate}`);
  showResult(plate);
}

// ====================================================================
// internals — UI helpers
// ====================================================================

/**
 * @param {number} percent
 * @param {string} text
 */
function setProgress(percent, text) {
  const container = document.getElementById(IDS.progressContainer);
  if (container) container.style.display = percent > 0 ? 'block' : 'none';
  const bar = document.getElementById(IDS.progressBar);
  if (bar) /** @type {HTMLElement} */ (bar).style.width = `${Math.max(0, Math.min(100, percent))}%`;
  const label = document.getElementById(IDS.progressText);
  if (label) label.textContent = text;
}

/** @param {string} plate */
function showResult(plate) {
  const el = document.getElementById(IDS.result);
  if (!el) return;
  el.innerHTML = `
    <div class="scanner-result-card">
      <div class="scanner-result-icon">✅</div>
      <div class="scanner-result-plate" dir="ltr">${plate}</div>
      <div class="scanner-actions">
        <button class="scanner-btn primary" data-action="use">חפש את הרכב הזה</button>
        <button class="scanner-btn" data-action="retry">סרוק שוב</button>
      </div>
    </div>
  `;
  el.querySelector('[data-action="use"]')?.addEventListener('click', () => usePlate(plate));
  el.querySelector('[data-action="retry"]')?.addEventListener('click', () => {
    clearResult();
    lastDetected = '';
    detectionCount = 0;
    startAutoScan();
  });
}

function clearResult() {
  const el = document.getElementById(IDS.result);
  if (el) el.innerHTML = '';
}
